import { createApp } from "vue";
import { api } from "jiffy-api";

import { getFileFromUrl } from "common/utils/file";
import ScrollToElement from "common/utils/ui/scroll-to-element";
import { getQueryParams, removeQueryParam } from "common/utils/browser";
import store from "./store/store";
import i18n from "../vue-cart/vue-i18n";
import defaultSurfaces from "./misc/default-surfaces";
import ProductDetailsPrint from "./ProductDetailsPrint.vue";
import { MODES, SURFACES, SIDES } from "./misc/constants";

const $getProductPageContainer = () => document.getElementById("product-page-container");

const initEditMode = async () => {
  const isEdit = Boolean(window.jiffy_made_config?.is_edit);
  if (!isEdit) return;

  const { asgid } = getQueryParams();
  if (!asgid) return;

  store.dispatch("app/restore", { asgid });
};

const initAutoUpload = async () => {
  const { artwork_urls: imageUrls, file_content_type: imageType, side } = getQueryParams();
  if (!imageUrls || !imageType) return;

  // Determine which side to attach artwork to
  // Only accept 'back', otherwise default to 'front'
  const targetSide = side === "back" ? "back" : "front";

  removeQueryParam("artwork_urls");
  removeQueryParam("file_content_type");
  removeQueryParam("side");

  try {
    const imageUrl = Object.values(JSON.parse(imageUrls)).pop();
    const [file] = await Promise.all([
      getFileFromUrl(imageUrl, `artwork_image-${Date.now()}`, imageType),
      store.dispatch(`artwork_${targetSide}/removeArtwork`)
    ]);
    store.dispatch(`artwork_${targetSide}/upload`, { originalFile: file, suppressEvents: true });
    ScrollToElement.scrollIntoViewBySelector("#vue-product-details-print");
  } catch (error) {
    console.error("Error during auto upload:", error);
  }
};

const fetchMadePreset = async () => {
  try {
    const { front, back } = await api.printOnDemand.getMadePreset();

    if (front) {
      store.dispatch("artwork_front/setDataFromPreset", front);
      store.commit("surface_front/setDataFromPreset", front);
    }
    if (back) {
      store.dispatch("artwork_back/setDataFromPreset", back);
      store.commit("surface_back/setDataFromPreset", back);
    }
  } catch (e) {
    // eslint-disable-next-line
    console.warn(e);
  } finally {
    $getProductPageContainer()?.classList.add("made-preset-loaded");
  }
};

const initMadePresets = () => {
  store.watch(
    state => state.app.mode,
    mode => {
      if (mode === MODES.MADE && !window.jiffy_made_config?.is_edit) {
        fetchMadePreset();
      }
    },
    { immediate: true }
  );
};

export const initDefaultProps = async () => {
  store.commit("product/setDefaultData", window.jiffy_made_product_props);
  store.dispatch("creation/fetchAreas");
  store.commit("setConfig", window.jiffy_made_config);

  store.commit("surface_front/setDefaultSurface", defaultSurfaces.front);
  store.commit("surface_back/setDefaultSurface", defaultSurfaces.back);
  store.commit("surface_front/setSurfaceLabel", SURFACES.FRONT);
  store.commit("surface_back/setSurfaceLabel", SURFACES.BACK);
  store.commit("artwork_front/setSide", SIDES.FRONT);
  store.commit("artwork_back/setSide", SIDES.BACK);
  store.commit("preset_front/setSide", SIDES.FRONT);
  store.commit("preset_back/setSide", SIDES.BACK);

  store.dispatch("artwork_front/createDtfChannel");
  store.dispatch("artwork_back/createDtfChannel");
  await store.dispatch("product/fetchProduct");
  initMadePresets();
};

const initPageData = () => {
  const activeColor = document.getElementById("active-color-name")?.value;
  const activeCompositionId = document.querySelector(
    `.js-color-variant-selector[data-color="${activeColor}"]`
  )?.dataset?.compositionId;

  if (activeColor && activeCompositionId) {
    store.dispatch("app/setActiveComposition", {
      compositionId: activeCompositionId,
      color: activeColor
    });
  }
};

const initProductDetailsPrint = () => {
  const $wrapper = document.getElementById("vue-product-details-print");

  if (!$wrapper) return;

  const ProductDetailsPrintWrapper = createApp({});

  ProductDetailsPrintWrapper.component("ProductDetailsPrint", ProductDetailsPrint);
  ProductDetailsPrintWrapper.use(store);
  ProductDetailsPrintWrapper.use(i18n);

  initDefaultProps();
  initPageData();
  initAutoUpload();
  initEditMode();

  ProductDetailsPrintWrapper.mount("#vue-product-details-print");
};

export default initProductDetailsPrint;
